import type { EmojiFrequencyStore } from '~publish/helpers/emojiFrequency'
import type {
  NoteReaction,
  NoteReactionAccount,
} from '~publish/hooks/notes/notesQueries'

/**
 * Note reactions keep their own use counts: emoji typed into post copy do not
 * become quick reactions, and a reaction does not reorder the Composer's
 * frequently used emoji. Only a tap that adds a reaction counts as a use.
 */
export const NOTE_REACTION_EMOJI_STORE: EmojiFrequencyStore = {
  frequencyKey: 'buffer_note_reaction_emoji_frequency',
  lastUsedKey: 'buffer_note_reaction_emoji_last_used',
}

/** Shown when the account has no reaction history yet. */
export const DEFAULT_QUICK_REACTIONS = ['👍', '👀', '✅'] as const
export const QUICK_REACTION_COUNT = 3

/**
 * The quick reactions are the account's most used emoji, topped up with the
 * defaults so the toolbar always offers the same number of choices.
 */
export function quickReactions(frequentlyUsed: readonly string[]): string[] {
  const emojis: string[] = []
  for (const emoji of [...frequentlyUsed, ...DEFAULT_QUICK_REACTIONS]) {
    if (!emojis.includes(emoji)) emojis.push(emoji)
    if (emojis.length === QUICK_REACTION_COUNT) break
  }
  return emojis
}

function accountLabel(account: NoteReactionAccount): string {
  return account.name?.trim() || account.email?.trim() || 'Unknown teammate'
}

/** The most people a reaction's description names before it counts the rest. */
export const NAMED_REACTORS_LIMIT = 3

/**
 * "Ada, Bo and you reacted with 👍". The current account is always "you" and
 * comes last, so the names of teammates read first. A busy group names the
 * first few and counts the rest, "Ada, you and 12 others", so a Note near its
 * reaction limit does not produce a tooltip hundreds of names long.
 */
export function describeNoteReaction(
  reaction: NoteReaction,
  currentAccountId: string | null,
): string {
  const others = reaction.accounts
    .filter(({ id }) => id !== currentAccountId)
    .map(accountLabel)
  const names = reaction.hasReacted ? [...others, 'you'] : others
  if (names.length > NAMED_REACTORS_LIMIT) {
    const namedOthers = others.slice(
      0,
      NAMED_REACTORS_LIMIT - (reaction.hasReacted ? 2 : 1),
    )
    const named = reaction.hasReacted ? [...namedOthers, 'you'] : namedOthers
    const rest = names.length - named.length
    return `${named.join(', ')} and ${rest} ${rest === 1 ? 'other' : 'others'} reacted with ${reaction.emoji}`
  }
  const people =
    names.length > 1
      ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
      : (names[0] ?? 'Nobody')
  return `${people} reacted with ${reaction.emoji}`
}
